import { matchPath } from 'react-router-dom';
import type { MembershipRole } from '@monecolier/api-client';

// UX-04 : extrait de NavBar.tsx (UX-01) pour être réutilisé aussi par Home.tsx
// (accès rapide par rôle, même filtrage que les menus de la NavBar) — SEULE source
// de vérité frontend pour ces deux listes, copie du garde-fou déjà appliqué côté
// backend (`SCHOOL_ADMIN_ONLY`/`PAYMENT_COLLECTORS`, voir apps/api/src/lib/
// permissions.ts et les routes correspondantes).
export const MANAGEMENT_ROLES: MembershipRole[] = ['SCHOOL_ADMIN'];
// AUT-12 : copie frontend de `PAYMENT_COLLECTORS` (apps/api/src/lib/permissions.ts)
// — `ADMIN_FINANCIER` ajouté, même élargissement que côté backend.
export const PAYMENT_ROLES: MembershipRole[] = ['SCHOOL_ADMIN', 'CASHIER', 'ADMIN_FINANCIER'];

// AUT-09 : copie frontend de `STUDENT_MANAGERS` (apps/api/src/lib/permissions.ts)
// — élève/inscriptions (SCH-06/SCH-12/ENR-02) désormais accessibles à STAFF, pas
// seulement SCHOOL_ADMIN. Délibérément DISTINCT de `MANAGEMENT_ROLES` : les autres
// routes de gestion d'établissement (Classes/Années scolaires/Tarifs/Fournitures,
// `/students-overview`) restent `SCHOOL_ADMIN_ONLY` côté backend, non élargies par
// ce ticket — voir Sidebar.tsx pour le détail de ce qui reste gardé par
// `MANAGEMENT_ROLES` malgré cet ajout.
//
// AUT-27 : `GESTION_ELEVES` ajouté — SEULE constante de ce fichier élargie pour ce
// nouveau rôle (ticket explicite : ne voit QUE "Élèves & Inscriptions" + Accueil
// dans la Sidebar) — `MANAGEMENT_ROLES`/`PAYMENT_ROLES`/`FINANCIAL_ROLES` restent
// STRICTEMENT inchangées, ce rôle n'a jamais accès à Comptabilité/Caisse/Tarifs/
// Paramètres/Équipe.
export const STUDENT_ROLES: MembershipRole[] = ['SCHOOL_ADMIN', 'STAFF', 'GESTION_ELEVES'];

// AUT-12 : copie frontend de `FINANCIAL_MANAGERS` (apps/api/src/lib/permissions.ts)
// — gate le périmètre financier strict (Tarifs, Dashboard financier/Factures
// impayées, avoir sur paiement), DISTINCT de `MANAGEMENT_ROLES` : ADMIN_FINANCIER
// n'a AUCUN accès élève/inscriptions/équipe, contrairement à SCHOOL_ADMIN — voir
// Sidebar.tsx pour le détail de ce qui reste réservé à `MANAGEMENT_ROLES` seul.
export const FINANCIAL_ROLES: MembershipRole[] = ['SCHOOL_ADMIN', 'ADMIN_FINANCIER'];

// SCH-53 : copie frontend de `TEACHER_ROLES` (apps/api/src/lib/permissions.ts)
// — infrastructure de rôle UNIQUEMENT pour ce ticket (aucune fonctionnalité
// réelle, gate seulement la nouvelle entrée Sidebar/TeacherHome.tsx).
// Groupe SÉPARÉ, jamais ajouté à `FINANCIAL_ROLES`/`PAYMENT_ROLES`/
// `STUDENT_ROLES`/`MANAGEMENT_ROLES` : décision produit tranchée du
// brainstorm espace prof (Q16) — AUCUN accès financier, jamais, sous aucune
// circonstance.
export const TEACHER_ROLES: MembershipRole[] = ['TEACHER'];

/**
 * AUT-26 — un membre peut cumuler PLUSIEURS rôles (`me.roles`, `GET /me`) : accès =
 * UNION, jamais une comparaison sur un seul rôle. Remplace `X_ROLES.includes(role)`
 * partout où le frontend décide d'un affichage/accès par rôle.
 */
export function hasAnyRole(roles: MembershipRole[], allowed: MembershipRole[]): boolean {
  return roles.some((role) => allowed.includes(role));
}

// UX-79 — copie frontend de `PAYMENT_ROSTER_VIEWERS` (apps/api/src/lib/
// permissions.ts) : garde de `GET /students`, l'appel qui charge `/students`.
// Plus large que `STUDENT_ROLES` : `ADMIN_FINANCIER` y arrive légitimement par
// le drill-down DSH-04 (`/students?studentId=`), même sans lien Sidebar.
export const STUDENT_ROSTER_ROLES: MembershipRole[] = [
  'SCHOOL_ADMIN',
  'STAFF',
  'CASHIER',
  'ADMIN_FINANCIER',
  'GESTION_ELEVES',
];

// UX-79 — copie frontend de `CLASS_MANAGEMENT_ROLES` (apps/api/src/lib/
// permissions.ts) : emploi du temps d'une classe (SCH-57).
export const CLASS_TIMETABLE_ROLES: MembershipRole[] = ['SCHOOL_ADMIN', 'TEACHER'];

/**
 * UX-79 — SEULE table frontend "route -> rôles autorisés". Consommée par :
 * - `RequireRouteAccess` (App.tsx), qui redirige vers l'accueil tout compte
 *   dont aucun rôle n'y figure — y compris sur un lien profond ;
 * - `Sidebar.tsx`, qui ne rend jamais un lien vers une route refusée ici.
 *
 * Règle de remplissage : chaque entrée recopie la garde BACKEND de l'appel qui
 * charge la page (jamais une règle nouvelle — ce ticket ne change AUCUN droit).
 * Une route absente de la table est ouverte à tout compte connecté (`/`,
 * `/account`, `/profile`, `/change-password`, `/mfa-setup`, `/schools/new`,
 * `/accept-invitation`...).
 *
 * ⚠️ Ne décide QUE de l'accès à une ROUTE. Un 403 renvoyé par une action À
 * L'INTÉRIEUR d'une page (édition d'emploi du temps, présence...) reste affiché
 * par la page et ne redirige jamais.
 */
export const ROUTE_ACCESS: Record<string, readonly MembershipRole[]> = {
  // Élèves & Inscriptions
  '/students-overview': MANAGEMENT_ROLES,
  '/students': STUDENT_ROSTER_ROLES,
  // UX-80 — fiche élève en page : MÊME garde que la liste (décision PO), le
  // drill-down DSH-04 y mène ADMIN_FINANCIER. Chaque section de la page
  // gère ensuite son propre refus backend (voir StudentDetail.tsx).
  '/students/:studentId': STUDENT_ROSTER_ROLES,
  '/student-duplicates': MANAGEMENT_ROLES,
  '/enrollments': STUDENT_ROLES,
  // UX-81 — fiche d'une demande en page : MÊME garde que la liste, qui est
  // aussi celle de l'appel qui charge la page (`GET /enrollments/:id`,
  // STUDENT_MANAGERS). Les documents (garde plus étroite) gèrent leur propre
  // refus dans la page (voir EnrollmentDetail.tsx).
  '/enrollments/:enrollmentId': STUDENT_ROLES,
  // Enseignant (chaque page appelle un endpoint `.../mine` réservé à TEACHER)
  '/teacher-home': TEACHER_ROLES,
  '/teacher-classes': TEACHER_ROLES,
  '/teacher-schedule': TEACHER_ROLES,
  '/teacher-attendance': TEACHER_ROLES,
  '/teacher-homework': TEACHER_ROLES,
  '/teacher-grades': TEACHER_ROLES,
  '/teacher-messaging': TEACHER_ROLES,
  '/classes/:classId/timetable': CLASS_TIMETABLE_ROLES,
  // UX-86 — « Enseignant principal » d'une classe : même garde que l'emploi du
  // temps (le droit de gestion réel vient du serveur, `canEdit`).
  '/classes/:classId/teacher': CLASS_TIMETABLE_ROLES,
  // Comptabilité
  '/accounting-overview': PAYMENT_ROLES,
  '/payments': PAYMENT_ROLES,
  '/payment-history': PAYMENT_ROLES,
  '/payment-proofs': PAYMENT_ROLES,
  '/financial-dashboard': FINANCIAL_ROLES,
  '/financial-by-class': FINANCIAL_ROLES,
  '/financial-by-class/:classId': FINANCIAL_ROLES,
  '/overdue-invoices': FINANCIAL_ROLES,
  '/credit-ledger': FINANCIAL_ROLES,
  '/fees': FINANCIAL_ROLES,
  // Paramètres. `/classes` : `GET /classes` est `PAYMENT_COLLECTORS` — la page
  // reste consultable par CASHIER/ADMIN_FINANCIER comme avant (SCH-61).
  '/classes': PAYMENT_ROLES,
  '/settings': MANAGEMENT_ROLES,
  '/onboarding': MANAGEMENT_ROLES,
  '/branding': MANAGEMENT_ROLES,
  '/school-years': MANAGEMENT_ROLES,
  '/payment-methods': MANAGEMENT_ROLES,
  '/document-requirements': MANAGEMENT_ROLES,
  '/invitations': MANAGEMENT_ROLES,
  '/whatsapp-broadcasts': MANAGEMENT_ROLES,
  '/audit-logs': MANAGEMENT_ROLES,
};

/** Rôles exigés par `pathname` (motif `:param` résolu), `null` = route ouverte. */
export function requiredRolesForPath(pathname: string): readonly MembershipRole[] | null {
  for (const [pattern, allowed] of Object.entries(ROUTE_ACCESS)) {
    if (matchPath({ path: pattern, end: true }, pathname)) return allowed;
  }
  return null;
}

export function canAccessRoute(roles: MembershipRole[], pathname: string): boolean {
  const allowed = requiredRolesForPath(pathname);
  return allowed === null || hasAnyRole(roles, [...allowed]);
}

/**
 * Accueil d'un compte : `/teacher-home` pour un TEACHER pur (la Sidebar ne lui
 * montre pas de lien "Accueil", SCH-65), `/` pour tous les autres.
 */
export function homePathForRoles(roles: MembershipRole[]): string {
  return roles.length === 1 && roles[0] === 'TEACHER' ? '/teacher-home' : '/';
}
